/**
 * Dawson Complete Dental Exam — 17 Section Configuration
 */

const EXAM_SECTIONS = [
  {
    id: 'history',
    number: 1,
    name: 'Pre-Clinical Patient Interview',
    shortName: 'Pre-Clinical Patie...',
    type: 'mixed',
    fields: [
      { id: 'chiefComplaint', label: 'Chief Complaint (pain, restorative, esthetic, TMD)', type: 'textarea' },
      { id: 'dentalHistory', label: 'Significant Dental History', type: 'textarea' },
      { id: 'estheticConcerns', label: 'Esthetic Concerns', type: 'radio', options: ['LOW', 'MEDIUM', 'HIGH'] },
      { id: 'estheticNotes', label: '', type: 'textarea', linkedTo: 'estheticConcerns' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.chiefComplaint) items.push({ label: 'Patient history and complaints', icon: 'check' });
      if (d?.estheticConcerns) items.push({ label: `Esthetic concerns`, value: d.estheticConcerns, color: d.estheticConcerns === 'HIGH' ? 'red' : d.estheticConcerns === 'MEDIUM' ? 'yellow' : 'green' });
      return items;
    }
  },
  {
    id: 'cancer',
    number: 2,
    name: 'Oral Cancer Examination',
    shortName: 'Oral Cancer Exa...',
    type: 'wnlTable',
    rows: [
      'Head', 'Neck', 'Face', 'Ears', 'Eyes', 'Lips',
      'Buccal Mucosa', 'Tongue', 'Floor Of The Mouth',
      'The Oropharynx', 'Tonsils', 'Hard & Soft Palate',
      'Posterior Pharyngeal Wall'
    ],
    fields: [
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      const hasPathology = d?.rows && Object.values(d.rows).some(r => r.status === 'pathology');
      if (hasPathology) items.push({ label: 'Oral cancer examination is positive', icon: 'check' });
      else if (d?.rows) items.push({ label: 'Oral cancer examination WNL', icon: 'check', color: 'green' });
      return items;
    }
  },
  {
    id: 'tmjQuestions',
    number: 3,
    name: 'TMJ Questions',
    shortName: 'TMJ Questions',
    type: 'yesNoTable',
    questions: [
      'Have you ever been diagnosed with a problem with either jaw joint?',
      'Does your jaw click, pop, or make noise when you open or close?',
      'Do you have pain or tenderness in your jaw when you open, close or chew?',
      'Has your jaw ever locked open or closed?',
      'Do you have frequent headaches? If so how often or when?',
      'Do you clench or grind your teeth, or been told that you do?',
      'Do you have a history of trauma to your chin or jaw?'
    ],
    fields: [
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      const hasYes = d?.answers && Object.values(d.answers).some(a => a.answer === 'yes');
      if (hasYes) items.push({ label: 'Jaw problems', icon: 'check' });
      return items;
    }
  },
  {
    id: 'muscles',
    number: 4,
    name: 'Muscles',
    shortName: 'Muscles',
    type: 'musclePalpation',
    groups: [
      {
        name: 'Right Side',
        muscles: ['Temporalis (Anterior)', 'Temporalis (Middle)', 'Temporalis (Posterior)', 'Masseter (Origin)', 'Masseter (Body)', 'Masseter (Insertion)', 'Medial Pterygoid', 'Lateral Pterygoid', 'SCM', 'Posterior Digastric']
      },
      {
        name: 'Left Side',
        muscles: ['Temporalis (Anterior)', 'Temporalis (Middle)', 'Temporalis (Posterior)', 'Masseter (Origin)', 'Masseter (Body)', 'Masseter (Insertion)', 'Medial Pterygoid', 'Lateral Pterygoid', 'SCM', 'Posterior Digastric']
      }
    ],
    fields: [
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.rightSide || d?.leftSide) items.push({ label: 'Muscle palpation issues' });
      if (d?.rightSide) items.push({ label: 'Right side', icon: 'check' });
      if (d?.leftSide) items.push({ label: 'Left side', icon: 'check' });
      return items;
    }
  },
  {
    id: 'tmjExam1',
    number: 5,
    name: 'TMJ Exam 1',
    shortName: 'TMJ Exam 1',
    type: 'mixed',
    fields: [
      { id: 'symptomStatus', label: 'Symptom Status', type: 'radio', options: ['ASYMPTOMATIC', 'SYMPTOMATIC'] },
      { id: 'maxOpening', label: 'Maximum Opening (mm)', type: 'number' },
      { id: 'protrusion', label: 'Protrusion (mm)', type: 'number' },
      { id: 'rtLateral', label: 'Rt Lateral (mm)', type: 'number' },
      { id: 'ltLateral', label: 'Lt Lateral (mm)', type: 'number' },
      { id: 'deviation', label: 'Deviation', type: 'radio', options: ['NONE', 'RIGHT', 'LEFT'] },
      { id: 'deflection', label: 'Deflection', type: 'radio', options: ['NONE', 'RIGHT', 'LEFT'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.symptomStatus) items.push({ label: d.symptomStatus, color: d.symptomStatus === 'SYMPTOMATIC' ? 'yellow' : 'green' });
      if (d?.protrusion) items.push({ label: `Protrusion`, value: d.protrusion, color: d.protrusion < 8 ? 'red' : 'green' });
      if (d?.ltLateral) items.push({ label: `Lt Lateral`, value: d.ltLateral, color: d.ltLateral < 8 ? 'red' : 'green' });
      return items;
    }
  },
  {
    id: 'tmjExam2',
    number: 6,
    name: 'TMJ Exam 2',
    shortName: 'TMJ Exam 2',
    type: 'mixed',
    fields: [
      { id: 'rightTMJ', label: 'Right TMJ', type: 'radio', options: ['NORMAL', 'TENDERNESS', 'PAIN', 'CREPITUS'] },
      { id: 'leftTMJ', label: 'Left TMJ', type: 'radio', options: ['NORMAL', 'TENDERNESS', 'TENSION', 'PAIN', 'CREPITUS'] },
      { id: 'slideRight', label: 'Slide from CR to CO — Right', type: 'radio', options: ['NORMAL', 'ABNORMAL'] },
      { id: 'slideLeft', label: 'Slide from CR to CO — Left', type: 'radio', options: ['NORMAL', 'ABNORMAL'] },
      { id: 'slideAnterior', label: 'Slide Anterior', type: 'checkbox' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      items.push({ label: 'TMJ:' });
      if (d?.rightTMJ && d.rightTMJ !== 'NORMAL') items.push({ label: `Right`, value: d.rightTMJ, color: 'red' });
      if (d?.leftTMJ && d.leftTMJ !== 'NORMAL') items.push({ label: `Left`, value: d.leftTMJ, color: d.leftTMJ === 'TENSION' ? 'yellow' : 'red' });
      if (d?.slideAnterior) items.push({ label: 'Slide', icon: 'check' });
      return items;
    }
  },
  {
    id: 'joints',
    number: 7,
    name: 'Joints',
    shortName: 'Joints',
    type: 'mixed',
    fields: [
      { id: 'rightDiagnosis', label: 'Right Diagnosis', type: 'select', options: ['NORMAL', 'MPCLICK', 'MPLOCK', 'DJD', 'SUBLUXATION', 'OTHER'] },
      { id: 'leftDiagnosis', label: 'Left Diagnosis', type: 'select', options: ['NORMAL', 'MPCLICK', 'MPLOCK', 'DJD', 'SUBLUXATION', 'OTHER'] },
      { id: 'rightNoise', label: 'Right Noise', type: 'select', options: ['NONE', 'CLICK', 'POP', 'CREPITUS'] },
      { id: 'leftNoise', label: 'Left Noise', type: 'select', options: ['NONE', 'CLICK', 'POP', 'CREPITUS'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.rightDiagnosis && d.rightDiagnosis !== 'NORMAL') items.push({ label: 'Right Diagnosis', value: d.rightDiagnosis, color: 'red' });
      if (d?.leftDiagnosis && d.leftDiagnosis !== 'NORMAL') items.push({ label: 'Left Diagnosis', value: d.leftDiagnosis, color: 'red' });
      if (d?.rightNoise && d.rightNoise !== 'NONE') items.push({ label: 'Right Noise', value: d.rightNoise, color: 'red' });
      if (d?.leftNoise && d.leftNoise !== 'NONE') items.push({ label: 'Left Noise', value: d.leftNoise, color: 'red' });
      return items;
    }
  },
  {
    id: 'occlusalExam',
    number: 8,
    name: 'Occlusal Examination',
    shortName: 'Occlusal Exam',
    type: 'mixed',
    fields: [
      { id: 'crowding', label: 'Crowding', type: 'radio', options: ['NONE', 'MILD', 'MODERATE', 'SEVERE'] },
      { id: 'wear', label: 'Wear', type: 'radio', options: ['NONE', 'MILD', 'MODERATE', 'SEVERE'] },
      { id: 'mobility', label: 'Mobility', type: 'radio', options: ['NONE', 'MILD', 'MODERATE', 'SEVERE'] },
      { id: 'fremitus', label: 'Fremitus', type: 'radio', options: ['NONE', 'PRESENT'] },
      { id: 'openBite', label: 'Open Bite', type: 'radio', options: ['NONE', 'ANTERIOR', 'POSTERIOR'] },
      { id: 'crossBite', label: 'Cross Bite', type: 'radio', options: ['NONE', 'ANTERIOR', 'POSTERIOR', 'BOTH'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.crowding && d.crowding !== 'NONE') items.push({ label: 'Crowding', value: d.crowding, color: 'yellow' });
      if (d?.wear && d.wear !== 'NONE') items.push({ label: 'Wear', value: d.wear, color: d.wear === 'SEVERE' ? 'red' : 'yellow' });
      return items;
    }
  },
  {
    id: 'occlusalStability',
    number: 9,
    name: 'Occlusal Stability Requirements',
    shortName: 'Occlusal Stability',
    type: 'mixed',
    fields: [
      { id: 'instability', label: 'Instability Present', type: 'checkbox' },
      { id: 'dawsonClass', label: 'Dawson Classification', type: 'select', options: ['I', 'IIA', 'IIB', 'III', 'IV', 'V'] },
      { id: 'angleClass', label: 'Angle Classification', type: 'select', options: ['I', 'II-Div1', 'II-Div2', 'III'] },
      { id: 'parafunction', label: 'Parafunction', type: 'select', options: ['NONE', 'CLENCH', 'BRUX', 'BOTH'] },
      { id: 'overjet', label: 'Overjet (mm)', type: 'number' },
      { id: 'overbite', label: 'Overbite (mm)', type: 'number' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.instability) items.push({ label: 'Instability', icon: 'check' });
      if (d?.dawsonClass) items.push({ label: 'Dawson Class', value: d.dawsonClass, color: 'yellow' });
      if (d?.angleClass) items.push({ label: 'Angle Class', value: d.angleClass, color: 'blue' });
      if (d?.parafunction && d.parafunction !== 'NONE') items.push({ label: 'Parafunction', value: d.parafunction, color: 'yellow' });
      return items;
    }
  },
  {
    id: 'airway',
    number: 10,
    name: 'Airway',
    shortName: 'Airway',
    type: 'mixed',
    fields: [
      { id: 'severeObstruction', label: 'Severe Obstruction', type: 'checkbox' },
      { id: 'tongueTie', label: 'Tongue Tie', type: 'radio', options: ['NONE', 'MILD', 'MODERATE', 'SEVERE'] },
      { id: 'mouthBreathing', label: 'Mouth Breathing', type: 'checkbox' },
      { id: 'snoring', label: 'Snoring', type: 'checkbox' },
      { id: 'osa', label: 'Known OSA Diagnosis', type: 'checkbox' },
      { id: 'cpap', label: 'CPAP Use', type: 'checkbox' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.severeObstruction) items.push({ label: 'Severe Obstruction', icon: 'check' });
      return items;
    }
  },
  {
    id: 'stopBang',
    number: 11,
    name: 'Stop Bang',
    shortName: 'Stop Bang',
    type: 'yesNoTable',
    questions: [
      'Do you snore loudly?',
      'Do you often feel tired, fatigued, or sleepy during the daytime?',
      'Has anyone observed you stop breathing during sleep?',
      'Are you being treated or have been treated for high blood pressure?',
      'BMI more than 35?',
      'Age over 50?',
      'Neck circumference greater than 40cm?',
      'Gender: Male?'
    ],
    scoreThresholds: { low: [0, 2], moderate: [3, 4], high: [5, 8] },
    fields: [
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      const yesCount = d?.answers ? Object.values(d.answers).filter(a => a.answer === 'yes').length : 0;
      const level = yesCount <= 2 ? 'ACCEPTABLE' : yesCount <= 4 ? 'MODERATE' : 'HIGH RISK';
      const color = yesCount <= 2 ? 'green' : yesCount <= 4 ? 'yellow' : 'red';
      if (d?.answers) items.push({ label: level, color });
      return items;
    }
  },
  {
    id: 'mallampati',
    number: 12,
    name: 'Mallampati Score',
    shortName: 'Mallampati',
    type: 'mixed',
    fields: [
      { id: 'score', label: 'Mallampati Classification', type: 'radio', options: ['CLASS 1', 'CLASS 2', 'CLASS 3', 'CLASS 4'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.score) {
        const c = d.score === 'CLASS 1' ? 'green' : d.score === 'CLASS 2' ? 'green' : 'yellow';
        items.push({ label: d.score, color: c });
      }
      return items;
    }
  },
  {
    id: 'periodontal',
    number: 13,
    name: 'Periodontal Screening',
    shortName: 'Periodontal',
    type: 'mixed',
    fields: [
      { id: 'psr', label: 'PSR Score (Periodontal Screening and Recording)', type: 'radio', options: ['0', '1', '2', '3', '4'] },
      { id: 'bleedingOnProbing', label: 'Bleeding on Probing', type: 'radio', options: ['NONE', 'LOCALIZED', 'GENERALIZED'] },
      { id: 'recession', label: 'Recession', type: 'radio', options: ['NONE', 'LOCALIZED', 'GENERALIZED'] },
      { id: 'pocketDepths', label: 'Notable Pocket Depths', type: 'textarea' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.psr) items.push({ label: 'PSR', value: d.psr, color: parseInt(d.psr) <= 2 ? 'green' : 'red' });
      if (d?.bleedingOnProbing && d.bleedingOnProbing !== 'NONE') items.push({ label: 'BOP', value: d.bleedingOnProbing, color: 'red' });
      return items;
    }
  },
  {
    id: 'radiographic',
    number: 14,
    name: 'Radiographic Findings',
    shortName: 'Radiographic',
    type: 'mixed',
    fields: [
      { id: 'panoramic', label: 'Panoramic Findings', type: 'textarea' },
      { id: 'periapical', label: 'Periapical Findings', type: 'textarea' },
      { id: 'cbct', label: 'CBCT Findings', type: 'textarea' },
      { id: 'boneLoss', label: 'Bone Loss', type: 'radio', options: ['NONE', 'MILD', 'MODERATE', 'SEVERE'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.boneLoss && d.boneLoss !== 'NONE') items.push({ label: 'Bone Loss', value: d.boneLoss, color: d.boneLoss === 'SEVERE' ? 'red' : 'yellow' });
      if (d?.panoramic) items.push({ label: 'Panoramic reviewed', icon: 'check' });
      return items;
    }
  },
  {
    id: 'dentalCharting',
    number: 15,
    name: 'Dental Charting',
    shortName: 'Dental Charting',
    type: 'mixed',
    fields: [
      { id: 'missingTeeth', label: 'Missing Teeth', type: 'textarea' },
      { id: 'existingRestorations', label: 'Existing Restorations', type: 'textarea' },
      { id: 'caries', label: 'Caries', type: 'textarea' },
      { id: 'fractures', label: 'Fractures / Cracks', type: 'textarea' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.missingTeeth) items.push({ label: 'Missing teeth noted', icon: 'check' });
      if (d?.caries) items.push({ label: 'Caries present', icon: 'check', color: 'red' });
      return items;
    }
  },
  {
    id: 'esthetic',
    number: 16,
    name: 'Esthetic Evaluation',
    shortName: 'Esthetic',
    type: 'mixed',
    fields: [
      { id: 'smileLine', label: 'Smile Line', type: 'radio', options: ['LOW', 'AVERAGE', 'HIGH'] },
      { id: 'gingivalDisplay', label: 'Gingival Display', type: 'radio', options: ['IDEAL', 'EXCESSIVE', 'DEFICIENT'] },
      { id: 'toothProportion', label: 'Tooth Proportions', type: 'radio', options: ['IDEAL', 'SHORT', 'NARROW', 'WIDE'] },
      { id: 'shade', label: 'Current Shade', type: 'textarea' },
      { id: 'midlineDeviation', label: 'Midline Deviation', type: 'radio', options: ['NONE', 'RIGHT', 'LEFT'] },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.smileLine) items.push({ label: 'Smile Line', value: d.smileLine, color: d.smileLine === 'HIGH' ? 'yellow' : 'green' });
      if (d?.gingivalDisplay && d.gingivalDisplay !== 'IDEAL') items.push({ label: 'Gingival', value: d.gingivalDisplay, color: 'yellow' });
      return items;
    }
  },
  {
    id: 'riskAssessment',
    number: 17,
    name: 'Risk Assessment',
    shortName: 'Risk Assessment',
    type: 'mixed',
    fields: [
      { id: 'overallRisk', label: 'Overall Risk Level', type: 'radio', options: ['LOW', 'MODERATE', 'HIGH'] },
      { id: 'biomechanicalRisk', label: 'Biomechanical Risk', type: 'radio', options: ['LOW', 'MODERATE', 'HIGH'] },
      { id: 'functionalRisk', label: 'Functional Risk', type: 'radio', options: ['LOW', 'MODERATE', 'HIGH'] },
      { id: 'diagnosis', label: 'Diagnosis Summary', type: 'textarea' },
      { id: 'treatmentPlan', label: 'Treatment Plan Overview', type: 'textarea' },
      { id: 'notes', label: 'Notes', type: 'textarea' },
    ],
    getSummary: (d) => {
      const items = [];
      if (d?.overallRisk) items.push({ label: 'Overall Risk', value: d.overallRisk, color: d.overallRisk === 'HIGH' ? 'red' : d.overallRisk === 'MODERATE' ? 'yellow' : 'green' });
      if (d?.diagnosis) items.push({ label: 'Diagnosis documented', icon: 'check' });
      return items;
    }
  }
];

export const getExamSections = () => EXAM_SECTIONS;
export const getExamSection = (id) => EXAM_SECTIONS.find(s => s.id === id);
export const getExamSectionByNumber = (num) => EXAM_SECTIONS.find(s => s.number === parseInt(num));
export default EXAM_SECTIONS;
